import { useState, useEffect, useRef, useMemo } from "react";
import { useDataStore } from "@store/user_org";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { useAtelierStore } from "@store/atelierStore";
import { usePlanningStore } from "@store/planningStore";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { StepService, StepDateTime, type BookingData, INITIAL, NEED_CARDS, ALL_SLOTS } from "@components/InternalBookingPage";
import { bookingsApi } from "@services/api/bookings";
import { TimeGridWithAppointments } from "@screens/reception/TimeGridWithAppointments";
import { WorkerTimeGrid, type DebugLayer } from "@screens/atelier/WorkerTimeGrid";
import type { AppointmentCategory } from "../types/garage/planning";
import type { GridPreview } from "@screens/atelier/gridTypes";

// Map booking service to reception grid category
function serviceToCategory(service: string, orgReceptions?: { id: string }[]): AppointmentCategory {
  if (service === "carrosserie") return "carrosserie";
  return orgReceptions?.[0]?.id || "reception_1";
}

// Parse "HH:MM" to decimal hour (e.g. "14:15" -> 14.25)
function timeToHour(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h + m / 60;
}

// Backend placement preview per slot ("HH:MM" -> chosen worker + start + reception lane).
type SlotPlacement = { worker_id: string; start_hour: number; ready_hour: number; reception_category?: string };

// Layer color definitions
const LAYER_COLORS = {
  buffer:  { color: "#F59E0B", bgColor: "#FEF3C7", label: "Buffer réception" },
  job:     { color: "#6366F1", bgColor: "#E0E7FF", label: "Travail" },
  safety:  { color: "#EF4444", bgColor: "#FEE2E2", label: "Marge sécurité" },
  lunch:   { color: "#9CA3AF", bgColor: "#E5E7EB", label: "Pause déjeuner" },
};

// ─── Main Screen ──────────────────────────────────────────

export default function BookingDebugScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id ?? "";
  const minDaysAhead = orgData?.config?.booking_min_days_ahead ?? 1;
  const maxDaysAhead = orgData?.config?.booking_max_days_ahead ?? 90;
  const srMinHoursAhead = orgData?.config?.booking_sr_min_hours_ahead ?? 2;

  // Booking config parameters (same defaults as backend)
  const bufferMinutes = orgData?.config?.sr_reception_buffer_minutes ?? 30;
  const safetyMarginHours = orgData?.config?.booking_safety_margin_hours ?? 1;
  const maxWaitMinutes = orgData?.config?.sr_max_wait_minutes ?? 60;
  const lunchStart = orgData?.config?.booking_lunch_start ?? 12;
  const lunchEnd = orgData?.config?.booking_lunch_end ?? 13;
  const openingHour = orgData?.config?.booking_opening_hour ?? 8;
  const closingHour = orgData?.config?.booking_closing_hour ?? 18;

  // Booking data — same state as the real widget
  const [data, setData] = useState<BookingData>({ ...INITIAL });
  const update = (key: keyof BookingData, value: unknown) => setData((prev) => ({ ...prev, [key]: value }));

  // Interventions — same fetch as the real widget
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  useEffect(() => { if (orgId) interventionsApi.list(orgId).then(setInterventions); }, [orgId]);

  // Multi-intervention model (same as the live booking page).
  const selectedIntvs = interventions.filter((i) => data.interventions.includes(i.name));
  const isFast = selectedIntvs.length > 0 && selectedIntvs.every((i) => i.fast);
  const estimatedMinutes = selectedIntvs.reduce((sum, i) => sum + (i.estimated_minutes || 0), 0);

  const [estimatedReadyTimes, setEstimatedReadyTimes] = useState<Record<string, string>>({});

  // Per-slot placement chosen by the REAL backend availability engine
  // (same source as "Prêt vers"). Drives the "Cible" worker + atelier ghost so
  // the debug screen never diverges from what client/internal booking would do.
  const [placements, setPlacements] = useState<Record<string, SlotPlacement>>({});

  // Debug override: navigate to any day (even "full"), with a no-availability hint.
  const [override, setOverride] = useState(false);
  const [overrideUnavail, setOverrideUnavail] = useState<Set<string>>(new Set());
  const [overrideReasons, setOverrideReasons] = useState<Record<string, string>>({});
  const [overrideLoaded, setOverrideLoaded] = useState(false);

  // Always hit the real availability endpoint for the selected day — this is the
  // single source of truth (client + internal booking use the same call). It
  // feeds the placement preview in BOTH modes and the blocked-slot hints in
  // override mode. getAvailability is cached (60s), so this is cheap.
  useEffect(() => {
    if (!data.deposit_date || !orgId) { setOverrideUnavail(new Set()); setOverrideReasons({}); setPlacements({}); setOverrideLoaded(false); return; }
    setOverrideLoaded(false);
    bookingsApi
      .getAvailability(data.deposit_date, data.service || "Mécanique", orgId, isFast || undefined, estimatedMinutes || undefined)
      .then((res) => {
        setOverrideUnavail(new Set(res.unavailable_slots || []));
        setOverrideReasons(res.unavailable_reasons || {});
        setPlacements(res.estimated_placements || {});
        if (res.estimated_ready_times) setEstimatedReadyTimes(res.estimated_ready_times);
        setOverrideLoaded(true);
      })
      .catch(() => { setOverrideUnavail(new Set()); setOverrideReasons({}); setPlacements({}); setOverrideLoaded(true); });
  }, [data.deposit_date, data.service, orgId, isFast, estimatedMinutes]);

  // ─── Drive the stores when the widget's date changes ───
  const atelierFetchData = useAtelierStore((s) => s.fetchData);
  const atelierSetDate = useAtelierStore((s) => s.setSelectedDate);
  const planningFetch = usePlanningStore((s) => s.fetchAppointments);
  const planningSetDate = usePlanningStore((s) => s.setSelectedDate);
  // Use the SAME store as the real atelier planning (AtelierScreen) so the debug
  // grid's greyed/hatched non-working hours match it exactly. useDisponibiliteStore
  // filters category="disponibilite" and silently drops service_rapide zones, which
  // made workers look off (hatched) here while the planning showed them working.
  const fetchBlocks = useDispoAtelierStore((s) => s.fetchBlocks);
  const blocks = useDispoAtelierStore((s) => s.blocks);
  const workers = useAtelierStore((s) => s.workers);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const fetchOrgMembers = useDataStore((s) => s.fetchOrgMembers);

  // Make sure the org's members are loaded — atelier worker columns are derived
  // from them (atelierStore.fetchData). On a hard refresh landing straight on this
  // screen they may not be loaded yet.
  useEffect(() => {
    if (orgId && orgMembers.length === 0) fetchOrgMembers(orgId);
  }, [orgId, orgMembers.length]);

  // Initial data load
  useEffect(() => {
    if (!orgId) return;
    planningFetch(orgId);
    fetchBlocks(orgId);
  }, [orgId]);

  // Fetch atelier data only once members exist, else fetchData builds an empty
  // worker list and its range-guard blocks any refetch → permanently blank grid.
  useEffect(() => {
    if (!orgId || orgMembers.length === 0) return;
    atelierFetchData(orgId);
  }, [orgId, orgMembers.length]);

  // Sync selected date to stores
  useEffect(() => {
    if (!data.deposit_date) return;
    atelierSetDate(data.deposit_date);
    planningSetDate(data.deposit_date);
  }, [data.deposit_date]);

  // ─── Preview computations ───
  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);

  const specialty = useMemo(() => {
    if (!selectedCard) return "mecanique";
    return selectedCard.service === "carrosserie" ? "carrosserie" : "mecanique";
  }, [selectedCard]);

  // Target worker for the job preview — taken DIRECTLY from the backend
  // availability engine (estimated_placements), the exact same source the real
  // client/internal booking uses. No client-side placement reimplementation, so
  // congé/pause exclusion and every other rule stay in one place. A worker on
  // congé (e.g. Jordan) is never returned here because the backend drops him.
  const targetWorker = useMemo(() => {
    if (!data.deposit_time || !data.deposit_date || !selectedCard) return null;
    const p = placements[data.deposit_time];
    if (!p) return null;
    const worker = workers.find((w) => w.id === p.worker_id);
    if (!worker) return null;
    return { worker, jobStart: p.start_hour };
  }, [data.deposit_time, data.deposit_date, selectedCard, placements, workers]);

  // Diagnostic info for debugging when preview doesn't show
  const debugInfo = useMemo(() => {
    if (!selectedCard || !data.deposit_time) return null;
    const matchCount = workers.filter((w) => w.specialty === specialty).length;
    const allSpecs = Array.from(new Set(workers.map((w) => w.specialty)));
    return {
      matchCount,
      allSpecs,
      estimatedMinutes,
      specialty,
    };
  }, [selectedCard, data.deposit_time, workers, specialty, estimatedMinutes]);

  // Reception previews: deposit + restitution ghosts
  const receptionPreviews = useMemo(() => {
    if (!data.deposit_time || !selectedCard) return [];
    const previews = [];
    const depositHour = timeToHour(data.deposit_time);

    // Draw the ghost on the reception lane the REAL algorithm would pick (first
    // free lane, from the backend), not always reception_1 — otherwise the ghost
    // falsely overlaps an existing RDV even though the booking goes to a free lane.
    const lane = placements[data.deposit_time]?.reception_category || serviceToCategory(selectedCard.service);

    // Deposit appointment preview
    previews.push({
      apptId: "__preview_deposit__",
      startHour: depositHour,
      durationHours: 0.25,
      category: lane,
    });

    // Restitution appointment preview (same lane as deposit, 7.5 min)
    const readyTimeStr = estimatedReadyTimes[data.deposit_time];
    if (readyTimeStr) {
      const readyHour = timeToHour(readyTimeStr);
      previews.push({
        apptId: "__preview_restitution__",
        startHour: readyHour,
        durationHours: 0.125,
        category: lane,
      });
    }

    return previews;
  }, [data.deposit_time, selectedCard, estimatedReadyTimes, placements]);

  // Atelier preview: ghost job at computed placement
  const atelierPreview = useMemo<GridPreview | null>(() => {
    if (!targetWorker || !data.deposit_date || estimatedMinutes <= 0) return null;
    const durationHours = estimatedMinutes / 60;
    return {
      type: "job",
      jobId: "__preview__",
      startHour: targetWorker.jobStart,
      durationHours,
      workerId: targetWorker.worker.id,
      date: data.deposit_date,
    };
  }, [targetWorker, data.deposit_date, estimatedMinutes]);

  // Debug layers: buffer, job, safety margin, lunch — all on the target worker column
  const debugLayers = useMemo<DebugLayer[]>(() => {
    if (!targetWorker || !data.deposit_time || !data.deposit_date) return [];
    const layers: DebugLayer[] = [];
    const wid = targetWorker.worker.id;
    const depositHour = timeToHour(data.deposit_time);
    const bufferHours = bufferMinutes / 60;
    const durationHours = estimatedMinutes / 60;

    // 1. Buffer: from deposit to earliest start
    if (bufferHours > 0) {
      layers.push({
        workerId: wid,
        startHour: depositHour,
        durationHours: bufferHours,
        ...LAYER_COLORS.buffer,
      });
    }

    // 2. Job work block (only if duration > 0)
    if (durationHours > 0) {
      layers.push({
        workerId: wid,
        startHour: targetWorker.jobStart,
        durationHours,
        ...LAYER_COLORS.job,
      });

      // 3. Safety margin: after job ends
      if (safetyMarginHours > 0) {
        const jobEnd = targetWorker.jobStart + durationHours;
        let safetyStart = jobEnd;
        // If safety margin starts during lunch, push to after lunch
        if (lunchStart < lunchEnd && safetyStart >= lunchStart && safetyStart < lunchEnd) {
          safetyStart = lunchEnd;
        }
        layers.push({
          workerId: wid,
          startHour: safetyStart,
          durationHours: safetyMarginHours,
          ...LAYER_COLORS.safety,
        });
      }
    }

    // 4. Lunch break (always show on target worker)
    if (lunchStart < lunchEnd) {
      layers.push({
        workerId: wid,
        startHour: lunchStart,
        durationHours: lunchEnd - lunchStart,
        ...LAYER_COLORS.lunch,
      });
    }

    return layers;
  }, [targetWorker, data.deposit_time, data.deposit_date, estimatedMinutes, bufferMinutes, safetyMarginHours, lunchStart, lunchEnd]);

  // WorkerTimeGrid refs (read-only, no drag)
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const jobDragRef = useRef(null);

  return (
    <div className="flex flex-col h-screen overflow-auto" style={{ background: "#FAFAFA" }}>
      {/* Top bar */}
      <div className="sticky top-0 z-30 flex flex-row items-center gap-4 px-6 py-3" style={{ borderBottom: "1px solid #E5E5E5", background: "#fff" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16 }}>Debug Disponibilités</span>
        {data.deposit_date && (
          <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#333", marginLeft: 8 }}>{data.deposit_date}</span>
        )}
        {data.deposit_time && (
          <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#10B981", fontWeight: 600 }}>{data.deposit_time}</span>
        )}
        {data.deposit_time && estimatedReadyTimes[data.deposit_time] && (
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#059669" }}>
            Prêt vers {estimatedReadyTimes[data.deposit_time].replace(":", "h")}
          </span>
        )}

        {/* Layer legend */}
        <div className="flex flex-row items-center gap-3 ml-auto">
          <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#999" }}>Couches :</span>
          {Object.entries(LAYER_COLORS).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1">
              <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.bgColor, borderLeft: `3px solid ${cfg.color}` }} />
              <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#666" }}>{cfg.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Config summary bar */}
      <div className="sticky top-[49px] z-25 flex flex-row items-center gap-4 px-6 py-1.5" style={{ borderBottom: "1px solid #E5E5E5", background: "#FAFAFA" }}>
        <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#999" }}>
          Ouverture: {openingHour}h-{closingHour}h | Buffer: {bufferMinutes}min | Marge sécurité: {safetyMarginHours}h | Max attente: {maxWaitMinutes}min | Déjeuner: {lunchStart}h-{lunchEnd}h
        </span>
        {targetWorker && (
          <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#6366F1", fontWeight: 600 }}>
            Cible : {targetWorker.worker.name} ({targetWorker.worker.specialty})
          </span>
        )}
        {debugInfo && !targetWorker && data.deposit_time && estimatedMinutes === 0 && (
          <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#F59E0B" }}>
            Durée estimée : 0 min (le moteur de dispo ne calcule pas de placement)
          </span>
        )}
        {debugInfo && !targetWorker && data.deposit_time && estimatedMinutes > 0 && (
          <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#EF4444", fontWeight: 600 }}>
            Aucun mécanicien {specialty} placé par le moteur de dispo sur ce créneau (congé/occupé/hors zone)
          </span>
        )}
      </div>

      {/* Main content: 3 columns */}
      <div className="flex flex-row flex-1">

        {/* LEFT: The ACTUAL booking widget Step 1 */}
        <div className="flex flex-col flex-shrink-0 sticky top-[77px] self-start overflow-y-auto" style={{ width: 380, maxHeight: "calc(100vh - 77px)", borderRight: "1px solid #E5E5E5", background: "#fff", padding: 16 }}>
          <StepService data={data} update={update} interventions={interventions} />
          <div style={{ height: 16 }} />

          {/* Debug override: go to ANY day, even "full" ones */}
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
            <span style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600, color: "#333" }}>Override — tous les jours</span>
          </label>

          {override ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="date"
                value={data.deposit_date || ""}
                onChange={(e) => { update("deposit_date", e.target.value); update("deposit_time", ""); }}
                style={{ fontFamily: "Poppins", fontSize: 14, padding: "10px 12px", border: "1.5px solid #E5E5E5", borderRadius: 10, outline: "none" }}
              />
              {data.deposit_date && overrideLoaded && overrideUnavail.size >= ALL_SLOTS.length && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                  <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#EA580C" }}>Aucune disponibilité n'est présente ce jour.</span>
                </div>
              )}
              {data.deposit_date && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ALL_SLOTS.map((s) => {
                    const unavail = overrideUnavail.has(s);
                    const sel = data.deposit_time === s;
                    return (
                      <button key={s} onClick={() => update("deposit_time", s)}
                        title={unavail ? (overrideReasons[s] || "Indisponible") : "Disponible"}
                        style={{
                          fontFamily: "Poppins", fontSize: 12, fontWeight: 500, padding: "6px 9px", borderRadius: 8, cursor: "pointer",
                          border: sel ? "2px solid #000" : `1.5px solid ${unavail ? "#FED7AA" : "#E5E5E5"}`,
                          background: sel ? "#000" : unavail ? "#FFF7ED" : "#fff",
                          color: sel ? "#fff" : unavail ? "#EA580C" : "#333",
                          textDecoration: unavail && !sel ? "line-through" : "none",
                        }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Reason for the selected (blocked) slot */}
              {data.deposit_time && overrideUnavail.has(data.deposit_time) && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#991B1B" }}>
                    <strong>{data.deposit_time} bloqué :</strong> {overrideReasons[data.deposit_time] || "Indisponible"}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <StepDateTime
              data={data}
              update={update}
              orgId={orgId}
              minDaysAhead={minDaysAhead}
              maxDaysAhead={maxDaysAhead}
              isFast={isFast}
              srMinHoursAhead={srMinHoursAhead}
              estimatedMinutes={estimatedMinutes}
              onEstimatedReadyTimes={setEstimatedReadyTimes}
            />
          )}
        </div>

        {/* MIDDLE: Reception planning — actual component */}
        <div className="flex flex-col flex-1 min-w-0" style={{ borderRight: "1px solid #E5E5E5" }}>
          <div className="sticky top-[77px] z-20 flex items-center justify-center py-2" style={{ background: "#fff", borderBottom: "1px solid #E5E5E5" }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#333" }}>
              Réception {data.deposit_date ? `- ${data.deposit_date}` : ""}
            </span>
          </div>
          <TimeGridWithAppointments externalPreviews={receptionPreviews} />
        </div>

        {/* RIGHT: Atelier planning — actual component */}
        <div ref={gridScrollRef} className="flex flex-col flex-1 min-w-0">
          <div className="sticky top-[77px] z-20 flex items-center justify-center py-2" style={{ background: "#fff", borderBottom: "1px solid #E5E5E5" }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#333" }}>
              Atelier {data.deposit_date ? `- ${data.deposit_date}` : ""}
            </span>
          </div>
          <WorkerTimeGrid
            gridScrollRef={gridScrollRef}
            gridPreview={atelierPreview}
            setGridPreview={() => {}}
            jobDragRef={jobDragRef}
            blocks={blocks}
            debugLayers={debugLayers}
          />
        </div>
      </div>
    </div>
  );
}
